/**
 * Mockup Migration Utility
 * 
 * Migrates legacy mockup structures to the new structure with fits and gender support.
 * This ensures backward compatibility while enabling the new features.
 * 
 * Architecture:
 * - Detects legacy structure: { [productId]: { [color]: { front?: string[], back?: string[] } } }
 * - Converts to new structure: { [productId]: { [color]: { [fit]: { [gender]: { front?: string[], back?: string[] } } } } }
 * - Uses default values: fit='regular', gender='unisex' for migrated mockups
 * - Preserves all existing mockup URLs
 * - Idempotent: Safe to run multiple times
 */

type LegacyMockupStructure = Record<
  string, // productId
  Record<
    string, // color
    {
      front?: string | string[];
      back?: string | string[];
      uploadedAt?: string;
    }
  >
>;

type NewMockupStructure = Record<
  string, // productId
  Record<
    string, // color
    {
      [fit in 'regular' | 'oversize']?: {
        [gender in 'men' | 'women' | 'unisex']?: {
          front?: string | string[];
          back?: string | string[];
          uploadedAt?: string;
        };
      };
    }
  >
>;

type MixedMockupStructure = Record<
  string, // productId
  Record<
    string, // color
    | {
        // New structure: With fits and gender
        [fit in 'regular' | 'oversize']?: {
          [gender in 'men' | 'women' | 'unisex']?: {
            front?: string | string[];
            back?: string | string[];
            uploadedAt?: string;
          };
        };
      }
    | {
        // Legacy structure: Without fits and gender
        front?: string | string[];
        back?: string | string[];
        uploadedAt?: string;
      }
  >
>;

/**
 * Checks if a mockup structure is in legacy format
 */
function isLegacyStructure(
  mockupData: any
): mockupData is LegacyMockupStructure {
  if (!mockupData || typeof mockupData !== 'object') {
    return false;
  }

  // Check if any product has legacy structure (direct front/back keys)
  for (const productId in mockupData) {
    const productData = mockupData[productId];
    if (!productData || typeof productData !== 'object') continue;

    for (const color in productData) {
      const colorData = productData[color];
      if (!colorData || typeof colorData !== 'object') continue;

      // Legacy structure has direct 'front' or 'back' keys
      if ('front' in colorData || 'back' in colorData) {
        // Check if it's NOT the new structure (doesn't have fit keys)
        if (!('regular' in colorData) && !('oversize' in colorData)) {
          return true;
        }
      }
    }
  }

  return false;
}

function toUrlArray(value: string | string[] | undefined): string[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function mergeSideUrls(
  existing: string[] | undefined,
  incoming: string | string[] | undefined
): string[] {
  const merged = [...(existing ?? [])];
  for (const url of toUrlArray(incoming)) {
    if (url && !merged.includes(url)) {
      merged.push(url);
    }
  }
  return merged;
}

/** Merges legacy top-level front/back arrays into regular.unisex when both exist. */
function normalizeHybridColorMockups(colorData: Record<string, unknown>) {
  const normalized = { ...colorData } as NewMockupStructure[string][string];

  if (!normalized.regular) {
    normalized.regular = {};
  }
  if (!normalized.regular.unisex) {
    normalized.regular.unisex = {};
  }

  const unisex = normalized.regular.unisex;
  if ("front" in colorData) {
    unisex.front = mergeSideUrls(
      toUrlArray(unisex.front as string | string[] | undefined),
      colorData.front as string | string[] | undefined
    );
    delete (normalized as Record<string, unknown>).front;
  }
  if ("back" in colorData) {
    unisex.back = mergeSideUrls(
      toUrlArray(unisex.back as string | string[] | undefined),
      colorData.back as string | string[] | undefined
    );
    delete (normalized as Record<string, unknown>).back;
  }
  if ("uploadedAt" in colorData && !unisex.uploadedAt) {
    unisex.uploadedAt = colorData.uploadedAt as string;
    delete (normalized as Record<string, unknown>).uploadedAt;
  }

  return normalized;
}

/**
 * Migrates legacy mockup structure to new structure with fits and gender
 * 
 * @param mockupData - The mockup data to migrate (can be legacy, new, or mixed)
 * @returns Migrated mockup data in new structure
 */
export function migrateMockupsToFitGenderStructure(
  mockupData: MixedMockupStructure | LegacyMockupStructure | null | undefined
): NewMockupStructure | null {
  if (!mockupData || typeof mockupData !== 'object') {
    return null;
  }

  const migrated: NewMockupStructure = {};

  for (const productId in mockupData) {
    const productData = mockupData[productId];
    if (!productData || typeof productData !== 'object') continue;

    migrated[productId] = {};

    for (const color in productData) {
      const colorData = productData[color];
      if (!colorData || typeof colorData !== 'object') continue;

      if ('regular' in colorData || 'oversize' in colorData) {
        if ('front' in colorData || 'back' in colorData) {
          migrated[productId][color] = normalizeHybridColorMockups(
            colorData as Record<string, unknown>
          );
        } else {
          migrated[productId][color] = colorData as any;
        }
        continue;
      }

      migrated[productId][color] = {
        regular: {
          unisex: {
            front: colorData.front,
            back: colorData.back,
            uploadedAt: colorData.uploadedAt,
          },
        },
      };
    }
  }

  return migrated;
}

/**
 * Migrates a single design's mockup data
 * This is a convenience function that can be called when loading a design
 */
export function migrateDesignMockups(design: {
  customMockupsByProductAndColor?: any;
}): {
  customMockupsByProductAndColor?: NewMockupStructure | null;
} {
  if (!design.customMockupsByProductAndColor) {
    return { customMockupsByProductAndColor: null };
  }

  const migrated = migrateMockupsToFitGenderStructure(
    design.customMockupsByProductAndColor
  );

  return {
    customMockupsByProductAndColor: migrated,
  };
}

/**
 * Checks if migration is needed for a design
 */
export function needsMockupMigration(design: {
  customMockupsByProductAndColor?: any;
}): boolean {
  if (!design.customMockupsByProductAndColor) {
    return false;
  }

  return isLegacyStructure(design.customMockupsByProductAndColor);
}

